// pothos manager «На печать» — постраничная проверка печатных PDF
// (IT/дизайнер), сконвертированных офлайн в JPEG-страницы.
// GET /api/getManagerPrintQa            → { batches }
// GET /api/getManagerPrintQa?batch=X    → { batches, pages: страницы пачки }
// POST /api/setPrintPageVerdict         → вердикт по странице
// GET /api/printPageImage?batch&page[&full=1] — бинарный JPEG (thumb/full).
import { z } from 'zod';

export const PothosPrintBatchSchema = z.object({
  batch: z.string(),
  merchant: z.string().default(''),
  country: z.string().default(''),
  pages: z.number().default(0),
  pending: z.number().default(0),
  accepted: z.number().default(0),
  rejected: z.number().default(0),
  has_source: z.boolean().default(false),
  source_name: z.string().default(''),
  source_bytes: z.number().default(0),
});
export type PothosPrintBatch = z.infer<typeof PothosPrintBatchSchema>;

export const PothosPrintPageSchema = z.object({
  batch: z.string(),
  page_no: z.number(),
  merchant: z.string().default(''),
  country: z.string().default(''),
  status: z.string().default('qa'),
  qa_by: z.string().default(''),
  code_value: z.string().default(''),
  holder_name: z.string().default(''),
  organizer: z.string().default(''),
  event_name: z.string().default(''),
  thumb_url: z.string().default(''),
  full_url: z.string().default(''),
  source_url: z.string().default(''),
});
export type PothosPrintPage = z.infer<typeof PothosPrintPageSchema>;

export const PothosManagerPrintQaResSchema = z.object({
  batches: z.array(PothosPrintBatchSchema).default([]),
  pages: z.array(PothosPrintPageSchema).default([]),
});
export type PothosManagerPrintQaRes = z.infer<typeof PothosManagerPrintQaResSchema>;

export const PothosSetPrintVerdictReqSchema = z.object({
  batch: z.string().trim().min(1).max(80),
  page_no: z.number().int().positive(),
  verdict: z.enum(['acc', 'rej']),
  comment: z.string().max(500).default(''),
});
export type PothosSetPrintVerdictReq = z.infer<typeof PothosSetPrintVerdictReqSchema>;
export const PothosSetPrintVerdictResSchema = z.object({ ok: z.boolean() });

// Бинарный printPageImage: контракт только на query (см. rules/contracts.md).
export const PothosPrintPageImageQuerySchema = z.object({
  batch: z.string().regex(/^[a-z0-9_]{1,80}$/),
  page: z.coerce.number().int().positive(),
  full: z.string().optional(),
});

// Бинарный printSourcePdf: исходный PDF пачки как есть. Отклонённые страницы
// вырезает клиент — так менеджер получает ровно тот файл, что уходил в печать,
// минус забракованное, без пересжатия и без потери качества.
export const PothosPrintSourceQuerySchema = z.object({
  batch: z.string().regex(/^[a-z0-9_]{1,80}$/),
});

// POST /api/rejectPrintEvent — «Отклонить мероприятие целиком»: все страницы
// пачки с этим событием (qa + принятые) уходят в rejected одной операцией.
export const PothosRejectPrintEventReqSchema = z.object({
  batch: z.string().trim().min(1).max(80),
  event_name: z.string().trim().min(1).max(300),
});
export type PothosRejectPrintEventReq = z.infer<typeof PothosRejectPrintEventReqSchema>;
export const PothosRejectPrintEventResSchema = z.object({ ok: z.boolean(), rejected: z.number() });

// POST /api/rejectPrintParticipant — «Удалить участника»: все страницы пачки
// с этим holder_name (qa + принятые) уходят в rejected одной операцией.
export const PothosRejectPrintParticipantReqSchema = z.object({
  batch: z.string().trim().min(1).max(80),
  holder_name: z.string().trim().min(1).max(300),
  // Ограничить удаление конкретной пачкой (событием) — иначе тёзка в другой
  // пачке того же батча не пострадает. '' = вся пачка батча.
  event_name: z.string().trim().max(300).default(''),
  // Причина: пусто → «некорректные личные данные» (основная причина).
  reason: z.string().trim().max(500).default(''),
});
export type PothosRejectPrintParticipantReq = z.infer<typeof PothosRejectPrintParticipantReqSchema>;
export const PothosRejectPrintParticipantResSchema = z.object({ ok: z.boolean(), rejected: z.number() });

// POST /api/acceptPrintRest — «Принять оставшееся»: все непроверенные (qa)
// страницы пачки становятся accepted одной операцией; отклонённые не трогаются.
export const PothosAcceptPrintRestReqSchema = z.object({
  batch: z.string().trim().min(1).max(80),
});
export type PothosAcceptPrintRestReq = z.infer<typeof PothosAcceptPrintRestReqSchema>;
export const PothosAcceptPrintRestResSchema = z.object({ ok: z.boolean(), accepted: z.number() });
